import Supercluster, { ClusterFeature } from 'supercluster'
import GeonJSONParser from './IGeoJSONParser'
import { BBOX, GeoJSONPoint, SuperClusterConfigOptions, TClusteredGroup } from './types'
import CacheClusteredGroup from './CacheClusteredGroup'
import { getConfig } from './api/cluster-manager-config'

function isGeoJsonPoint(point: unknown[]): point is GeoJSONPoint[] {
    return (point[0] as GeoJSONPoint)?.geometry !== undefined
}

export default class ClusteredGroup<T> {
    private cache: CacheClusteredGroup
    private parser: GeonJSONParser<T>
    private DEFAULT_BBOX: BBOX = [-180, -90, 180, 90]
    private config: SuperClusterConfigOptions

    constructor(key: string, points: Array<T>, parser: GeonJSONParser<T>) {
        this.cache = new CacheClusteredGroup()
        this.parser = parser
        if (points?.length > 0) this.loadPointsToGroup(key, points)
    }

    private handleSuperClusterConfig = async () => {
        ;(async () => await getConfig())().then(r => (r.success ? (this.config = r?.data) : null))
    }

    public filter(key: string, points: Array<T>) {
        const group = this.cache.get(key)
        let geoJsonPoints = isGeoJsonPoint(points) ? points : points.map(p => this.parser.to(p))
        const filteredIndex = group.filteredIndex.load(geoJsonPoints)
        this.cache.put(key, { ...group, filteredIndex, filtered: true })
    }

    public getConfig() {
        return this.config
    }

    public unfilter(key: string) {
        const group = this.cache.get(key)
        this.cache.put(key, { ...group, filtered: false })
    }

    private async initNewTClusteredGroup(): Promise<TClusteredGroup> {
        const config = await getConfig()
        return {
            points: [],
            index: config?.success
                ? new Supercluster({
                      minPoints: config.data.minPoints,
                      maxZoom: config.data.maxZoom,
                      minZoom: config.data.minZoom,
                      radius: config.data.radius,
                      extent: config.data.extent
                  })
                : new Supercluster(),
            filteredIndex: config.success
                ? new Supercluster({
                      minPoints: config.data.minPoints,
                      maxZoom: config.data.maxZoom,
                      minZoom: config.data.minZoom,
                      radius: config.data.radius,
                      extent: config.data.extent
                  })
                : new Supercluster(),
            visible: true,
            filtered: false
        }
    }

    public getIndexWithZoomByKey(key: string, zoom: number): Array<ClusterFeature<any>> {
        const group = this.cache.get(key)
        if (group.filtered) return group.filteredIndex.getClusters(this.DEFAULT_BBOX, zoom)
        return this.cache.get(key).index.getClusters(this.DEFAULT_BBOX, zoom)
    }

    public getIndexWithZoom(zoom: number): Map<string, Array<ClusterFeature<any>>> {
        const groups = this.cache.getCache()
        const clusterFeatureMap = new Map<string, Array<ClusterFeature<any>>>()

        for (const [key, value] of groups) {
            const clustersInZoom = value.filtered
                ? value.filteredIndex.getClusters(this.DEFAULT_BBOX, zoom)
                : value.index.getClusters(this.DEFAULT_BBOX, zoom)
            clusterFeatureMap.set(key, clustersInZoom)
        }

        return clusterFeatureMap
    }

    public async loadPointsToGroup(key: string, points: Array<T>): Promise<void> {
        const group: TClusteredGroup = this.cache.get(key) ? this.cache.get(key) : await this.initNewTClusteredGroup()

        if (isGeoJsonPoint(points)) {
            group.points = points
            group.index.load(points)
        } else {
            const converted = points.map(p => this.parser.to(p))
            group.points = converted
            group.index.load(converted)
        }

        this.cache.put(key, group)
    }
}
